import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Calendar, Sun, Moon, Utensils, ChevronRight, Check, X, Building2 } from 'lucide-react';

const AdminCMS = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [menus, setMenus] = useState({
    weekly: null,
    source: null,
    evening: null,
    thermes: null
  });

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return null;
    }
    return { 'Authorization': `Basic ${auth}` };
  };

  useEffect(() => {
    fetchAllMenus();
  }, []);

  const fetchAllMenus = async () => {
    const headers = getAuthHeader();
    if (!headers) return;

    try {
      const [weeklyRes, sourceRes, eveningRes, thermesRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/weekly-menu`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/menu-source`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/evening-menu`, { headers }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/cms/menu-thermes`, { headers })
      ]);

      const weekly = weeklyRes.ok ? await weeklyRes.json() : null;
      const source = sourceRes.ok ? await sourceRes.json() : null;
      const evening = eveningRes.ok ? await eveningRes.json() : null;
      const thermes = thermesRes.ok ? await thermesRes.json() : null;

      setMenus({ weekly, source, evening, thermes });
    } catch (error) {
      toast({ title: "Erreur", description: "Impossible de charger les menus", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const StatusBadge = ({ published, showOnSite }) => (
    <div className="flex items-center gap-2">
      {published ? (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Check size={12} className="mr-1" />
          Publié
        </span>
      ) : (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          Brouillon
        </span>
      )}
      {!showOnSite && (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
          <X size={12} className="mr-1" />
          Masqué
        </span>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Link to="/admin/dashboard" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft size={24} />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Gestion des Menus</h1>
              <p className="text-sm text-gray-500">Modifiez les menus affichés sur le site</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* MIDI Section */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Sun size={20} className="text-amber-500" />
            <h2 className="text-lg font-bold text-gray-900">Le Midi</h2>
          </div>

          {/* Weekly Menu */}
          <Link 
            to="/admin/cms/weekly-menu" 
            className="block bg-white rounded-lg shadow mb-4 hover:shadow-lg transition-shadow"
            data-testid="cms-weekly-menu-link"
          >
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-amber-100 p-3 rounded-full">
                  <Calendar className="text-amber-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Menu de la Semaine</h3>
                  <p className="text-sm text-gray-500">Menu à prix fixe, change chaque semaine</p>
                  {menus.weekly && (
                    <div className="mt-2">
                      <StatusBadge 
                        published={menus.weekly.is_published} 
                        showOnSite={menus.weekly.show_on_website} 
                      />
                    </div>
                  )}
                </div>
              </div>
              <ChevronRight className="text-gray-400" size={24} />
            </div>
          </Link>

          {/* Menu Source */}
          <Link 
            to="/admin/cms/menu-source" 
            className="block bg-white rounded-lg shadow hover:shadow-lg transition-shadow"
            data-testid="cms-menu-source-link"
          >
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-green-100 p-3 rounded-full">
                  <Utensils className="text-green-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Menu Source</h3>
                  <p className="text-sm text-gray-500">Menu déjeuner régulier, change par saison</p>
                  {menus.source && (
                    <div className="mt-2">
                      <StatusBadge 
                        published={menus.source.is_published} 
                        showOnSite={menus.source.show_on_website} 
                      />
                    </div>
                  )}
                </div>
              </div>
              <ChevronRight className="text-gray-400" size={24} />
            </div>
          </Link>
        </div>

        {/* SOIR Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Moon size={20} className="text-indigo-500" />
            <h2 className="text-lg font-bold text-gray-900">Le Soir</h2>
          </div>

          {/* Evening Menu (À la carte) */}
          <Link 
            to="/admin/cms/evening-menu" 
            className="block bg-white rounded-lg shadow mb-4 hover:shadow-lg transition-shadow"
            data-testid="cms-evening-menu-link"
          >
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-indigo-100 p-3 rounded-full">
                  <Utensils className="text-indigo-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Carte du Soir</h3>
                  <p className="text-sm text-gray-500">Menu à la carte avec prix par plat</p>
                  {menus.evening && (
                    <div className="mt-2">
                      <StatusBadge 
                        published={menus.evening.is_published} 
                        showOnSite={menus.evening.show_on_website} 
                      />
                    </div>
                  )}
                </div>
              </div>
              <ChevronRight className="text-gray-400" size={24} />
            </div>
          </Link>

          {/* Menu Thermes */}
          <Link 
            to="/admin/cms/menu-thermes" 
            className="block bg-white rounded-lg shadow hover:shadow-lg transition-shadow"
            data-testid="cms-menu-thermes-link"
          >
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-purple-100 p-3 rounded-full">
                  <Utensils className="text-purple-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Menu Thermes</h3>
                  <p className="text-sm text-gray-500">Menu à prix fixe, soirée & week-end</p>
                  {menus.thermes && (
                    <div className="mt-2">
                      <StatusBadge 
                        published={menus.thermes.is_published} 
                        showOnSite={menus.thermes.show_on_website} 
                      />
                    </div>
                  )}
                </div>
              </div>
              <ChevronRight className="text-gray-400" size={24} />
            </div>
          </Link>
        </div>

        {/* SETTINGS Section */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4">
            <Building2 size={20} className="text-gray-500" />
            <h2 className="text-lg font-bold text-gray-900">Paramètres</h2>
          </div>

          {/* Restaurant Info */}
          <Link 
            to="/admin/cms/restaurant-info" 
            className="block bg-white rounded-lg shadow hover:shadow-lg transition-shadow"
            data-testid="cms-restaurant-info-link"
          >
            <div className="p-6 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-gray-100 p-3 rounded-full">
                  <Building2 className="text-gray-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">Informations du Restaurant</h3>
                  <p className="text-sm text-gray-500">Téléphone, adresse, horaires</p>
                </div>
              </div>
              <ChevronRight className="text-gray-400" size={24} />
            </div>
          </Link>
        </div>

        {/* Help Text */}
        <div className="mt-8 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Conseil :</strong> Les modifications sont enregistrées en brouillon par défaut. 
            N'oubliez pas d'activer "Publié" pour que les changements apparaissent sur le site.
          </p>
        </div>
      </main>
    </div>
  );
};

export default AdminCMS;
